import { createRoute } from '@tanstack/react-router';
import { useState } from 'react';
import { useApi } from '../api/ApiProvider';
import {
  useBillingStatus,
  usePortalSession,
  useSyncBillingToHelper,
} from '../api/billing';
import { type PairResponse, pairFlow } from '../api/pairing';
import { useAppStore } from '../stores/app';
import { rootRoute } from './__root';

export const settingsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/settings',
  component: Settings,
});

const DEFAULT_HELPER_URL = 'http://127.0.0.1:7831';

function Settings() {
  const client = useApi();
  const sensitivity = useAppStore((s) => s.sensitivity);
  const setSensitivity = useAppStore((s) => s.setSensitivity);

  const [helperUrl, setHelperUrl] = useState(DEFAULT_HELPER_URL);
  const [pairing, setPairing] = useState(false);
  const [paired, setPaired] = useState<PairResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function onPair() {
    setError(null);
    setPaired(null);
    setPairing(true);
    try {
      const { paired } = await pairFlow(client, helperUrl);
      setPaired(paired);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setPairing(false);
    }
  }

  return (
    <section>
      <h1 style={{ marginTop: 0 }}>Settings</h1>

      <h2>Insight sensitivity</h2>
      <fieldset style={{ display: 'flex', gap: 12, border: 0, padding: 0 }}>
        {(['low', 'balanced', 'proactive'] as const).map((s) => (
          <label key={s} style={{ display: 'flex', gap: 4 }}>
            <input
              type="radio"
              name="sensitivity"
              value={s}
              checked={sensitivity === s}
              onChange={() => setSensitivity(s)}
            />
            {s}
          </label>
        ))}
      </fieldset>

      <h2 style={{ marginTop: 32 }}>Pair desktop helper</h2>
      <p style={{ color: '#666' }}>
        Per ADR-025: pairing requires a recent strong auth (enforced once that flow lands). Until
        BE-009 wires discovery, the web app submits the pairing token to the helper at the URL
        below.
      </p>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
        <input
          type="url"
          value={helperUrl}
          onChange={(e) => setHelperUrl(e.target.value)}
          style={{ width: 320, padding: 6 }}
          aria-label="Helper base URL"
        />
        <button type="button" onClick={onPair} disabled={pairing}>
          {pairing ? 'Pairing…' : 'Pair helper'}
        </button>
      </div>
      {error && (
        <p role="alert" style={{ color: 'crimson', marginTop: 12 }}>
          {error}
        </p>
      )}
      {paired && (
        <div style={{ marginTop: 12, padding: 12, background: '#eef9f0', borderRadius: 6 }}>
          <strong>Paired ✓</strong> — {paired.device_name} at{' '}
          {new Date(paired.paired_at).toLocaleString()}
        </div>
      )}

      <h2 style={{ marginTop: 32 }}>License</h2>
      <LicenseSection />

      <h2 style={{ marginTop: 32 }}>Other</h2>
      <p style={{ color: '#666' }}>BYOK, devices list, telemetry opt-out come in WEB-006.</p>
    </section>
  );
}

function LicenseSection() {
  const billing = useBillingStatus();
  const sync = useSyncBillingToHelper();
  const portal = usePortalSession();
  const [refreshedAt, setRefreshedAt] = useState<number | null>(null);

  const licenseState = describeLicenseState(billing.data);
  // Stripe Customer Portal upgrades don't hit /billing/success, so the helper's
  // local cache stays stale until this button runs the same sync flow.
  const onRefresh = async () => {
    try {
      await sync.mutateAsync();
      setRefreshedAt(Date.now());
    } catch {
      // Errors surface via sync.error below.
    }
  };
  const onManage = async () => {
    try {
      const { url } = await portal.mutateAsync();
      window.location.href = url;
    } catch {
      // Errors surface via portal.error below.
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      <div style={{ color: '#555', fontSize: 14 }}>{licenseState}</div>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
        <button type="button" onClick={onRefresh} disabled={sync.isPending}>
          {sync.isPending ? 'Refreshing…' : 'Refresh license'}
        </button>
        <button type="button" onClick={onManage} disabled={portal.isPending}>
          {portal.isPending ? 'Opening…' : 'Manage subscription'}
        </button>
        {refreshedAt && !sync.isPending && !sync.error && (
          <span style={{ color: '#047857', fontSize: 12 }}>
            Synced at {new Date(refreshedAt).toLocaleTimeString()}
          </span>
        )}
      </div>
      {sync.error && (
        <p role="alert" style={{ color: 'crimson', marginTop: 4 }}>
          Refresh failed: {(sync.error as Error).message}
        </p>
      )}
      {portal.error && (
        <p role="alert" style={{ color: 'crimson', marginTop: 4 }}>
          Portal failed: {(portal.error as Error).message}
        </p>
      )}
    </div>
  );
}

function describeLicenseState(
  status: import('../api/billing').BillingStatus | undefined,
): string {
  if (!status) return 'Loading…';
  const parts: string[] = [];
  if (status.has_perpetual_license) {
    parts.push('Desktop license active');
    if (status.license_purchased_at) {
      parts.push(
        `since ${new Date(status.license_purchased_at).toLocaleDateString()}`,
      );
    }
  } else if (status.trial_days_remaining > 0) {
    parts.push(`Trial: ${status.trial_days_remaining} day${status.trial_days_remaining === 1 ? '' : 's'} remaining`);
  } else if (status.trial_started_at) {
    parts.push('Trial expired');
  } else {
    parts.push('No license');
  }
  if (status.sub_tier !== 'free') {
    parts.push(`+ Cloud sub (${status.sub_tier})`);
  }
  return parts.join(' · ');
}
